import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { dutiesAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiGrid, FiX, FiChevronDown, FiStar, FiCheck, FiPhone, FiBell } from 'react-icons/fi';
import './Students.css';
import './ClientVertical.css';

// Quadrant accents stay semantic (urgency × importance, not a brand choice) —
// same palette Students.css already uses for status colour-coding, just
// applied here to Eisenhower-matrix quadrants instead of pipeline stages.
const QUADRANTS = [
  { key: 'Q1', title: 'Do Now', sub: 'Urgent + important', color: '#f5576c', bg: 'rgba(245,87,108,0.06)' },
  { key: 'Q2', title: 'Schedule', sub: 'Important, not urgent', color: '#4facfe', bg: 'rgba(79,172,254,0.07)' },
  { key: 'Q3', title: 'Delegate', sub: 'Urgent, not important', color: '#f5a623', bg: 'rgba(245,166,35,0.08)' },
  { key: 'Q4', title: 'Eliminate', sub: 'Neither — archive or drop', color: '#8b93a2', bg: '#f8fafc' },
];

// One colour per client type, used for the card's left edge and its label.
// Red and amber are left to the quadrants above. The colours are the shared
// vertical tokens in colour-tokens.css, the same as the Dashboard.
const KINDS = {
  STUDENT: { label: 'Student', plural: 'students', color: 'var(--vertical-students)', tint: 'var(--vertical-students-tint)', path: '/students' },
  JOB: { label: 'Job seeker', plural: 'job seekers', color: 'var(--vertical-jobs)', tint: 'var(--vertical-jobs-tint)', path: '/job-clients' },
  TOURIST: { label: 'Tourist', plural: 'tourists', color: 'var(--vertical-tourists)', tint: 'var(--vertical-tourists-tint)', path: '/tourist-clients' },
  MEDICAL: { label: 'Medical', plural: 'medical clients', color: 'var(--vertical-medical)', tint: 'var(--vertical-medical-tint)', path: '/medical-tourism' },
};
const KIND_ORDER = ['STUDENT', 'JOB', 'TOURIST', 'MEDICAL'];

// Reminders that are not client files (22 Oct 2026): new leads, the CMO's plan items, role duties.
const REMINDERS = {
  LEAD: { label: 'Lead', color: '#0891b2', tint: '#ecfeff' },
  PLAN: { label: 'Marketing', color: '#db2777', tint: '#fdf2f8' },
  DUTY: { label: 'My duty', color: '#475569', tint: '#f1f5f9' },
};

const boardAPI = {
  load: (params) => api.get('/action-board/', { params }),
  pin: (kind, id, quadrant) => api.post('/action-board/pin/', { kind, id, quadrant }),
  closeFollowUp: (id, note) => api.post(`/feedback/${id}/close_followup/`, { note }),
  delegate: (id, assignee, instruction) => api.post(`/feedback/${id}/delegate/`, { assignee, instruction }),
  assignTask: (id, assignee) => api.post(`/feedback-tasks/${id}/assign/`, { assignee }),
  finishTask: (id, note) => api.post(`/feedback-tasks/${id}/done/`, { note }),
};

const cardStyle = (kind) => ({
  background: '#fff', border: '1px solid #e0e0e0', borderLeft: `4px solid ${KINDS[kind]?.color || '#94a3b8'}`,
  borderRadius: 10, padding: '10px 12px', marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
});
const fieldStyle = { width: '100%', fontFamily: 'inherit', fontSize: 12.5, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' };

const KindChip = ({ kind }) => {
  const k = KINDS[kind];
  if (!k) return null;
  return (
    <span style={{ background: k.tint, color: k.color, fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>
      {k.label}
    </span>
  );
};

const Reasons = ({ items }) => (items?.length > 0 ? (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
    {items.map((r, i) => (
      <span key={i} style={{ background: '#f5f7fa', border: '1px solid #e8ecf4', color: '#64748b', fontSize: 10.5, borderRadius: 6, padding: '1px 6px' }}>{r}</span>
    ))}
  </div>
) : null);

const Contact = ({ name, phone }) => (
  <div style={{ color: '#64748b', fontSize: 11.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
    <FiPhone size={11} /> {name}{phone ? ` · ${phone}` : ' · no phone on file'}
  </div>
);

const ClientCard = ({ s, onOpen, onPin }) => {
  const [pinOpen, setPinOpen] = useState(false);
  return (
    <div style={cardStyle(s.kind)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div onClick={onOpen} style={{ cursor: 'pointer', minWidth: 0 }}>
          <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {s.name} <KindChip kind={s.kind} />
          </div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>{s.ref} · {s.status_label}{s.assigned_agent_name ? ` · ${s.assigned_agent_name}` : ''}</div>
        </div>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setPinOpen((o) => !o)} title="Pin to quadrant" className="cv-inline-btn"
            style={s.pinned ? { background: 'rgba(1, 31, 243,0.1)', borderColor: '#011ff3', color: '#0118c2' } : undefined}>
            {s.pinned ? 'Pinned' : 'Pin'} <FiChevronDown size={11} />
          </button>
          {pinOpen && (
            <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 4, width: 140, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
              {QUADRANTS.map((q) => (
                <div key={q.key} onClick={() => { onPin(q.key); setPinOpen(false); }}
                  style={{ padding: '6px 8px', fontSize: 12, color: q.color, cursor: 'pointer', borderRadius: 6 }}>
                  {q.key} · {q.title}
                </div>
              ))}
              <div onClick={() => { onPin(''); setPinOpen(false); }}
                style={{ padding: '6px 8px', fontSize: 12, color: '#94a3b8', cursor: 'pointer', borderTop: '1px solid #f1f5f9' }}>
                Clear pin (auto)
              </div>
            </div>
          )}
        </div>
      </div>
      <Reasons items={s.reasons} />
    </div>
  );
};

// A note box that must be filled before the action goes through.
const NoteAction = ({ label, placeholder, onSubmit, onCancel, children }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    if (!note.trim()) { setError('Write a note first.'); return; }
    setBusy(true);
    setError('');
    try { await onSubmit(note.trim()); } catch (e) { setError(e?.response?.data?.detail || 'Could not save.'); setBusy(false); }
  };
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {children}
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={placeholder} style={fieldStyle} />
      {error && <span style={{ color: '#b91c1c', fontSize: 11.5 }}>{error}</span>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="cv-inline-btn" onClick={go} disabled={busy}><FiCheck size={11} /> {label}</button>
        <button className="cv-inline-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
};

const StaffSelect = ({ staff, value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={fieldStyle}>
    <option value="">Choose a staff member…</option>
    {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
  </select>
);

// Leadership only: an answer that needs someone to act.
const FeedbackCard = ({ f, staff, onOpen, onChanged }) => {
  const [mode, setMode] = useState('');
  const [assignee, setAssignee] = useState('');
  const urgent = f.alert_level === 'URGENT';
  return (
    <div style={{ ...cardStyle(f.kind), background: urgent ? '#fff5f5' : '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <FiStar size={13} color="#f59e0b" /> {f.name} <KindChip kind={f.kind} />
            {urgent && <span style={{ background: '#b91c1c', color: '#fff', fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px' }}>Urgent</span>}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>
            Feedback · {f.moment_label} · answered by {f.respondent.toLowerCase()}
          </div>
        </div>
      </div>
      <Reasons items={f.reasons} />
      <Contact name={f.contact_name} phone={f.contact_phone} />
      <div style={{ color: f.overdue ? '#b91c1c' : '#64748b', fontSize: 11.5, marginTop: 4, fontWeight: f.overdue ? 700 : 400 }}>
        {f.overdue ? 'Overdue' : 'Due'} {f.due_at ? new Date(f.due_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
        {f.owner_name ? ` · owner ${f.owner_name}` : ''}
        {f.delegated_to.length > 0 ? ` · call handed to ${f.delegated_to.join(', ')}` : ''}
      </div>
      {!mode && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button className="cv-inline-btn" onClick={onOpen}>Read answers</button>
          <button className="cv-inline-btn" onClick={() => setMode('delegate')}>Hand off the call</button>
          <button className="cv-inline-btn" onClick={() => setMode('close')}><FiCheck size={11} /> Close</button>
        </div>
      )}
      {mode === 'close' && (
        <NoteAction label="Close follow-up" placeholder="What was done? e.g. Called her, refund agreed"
          onCancel={() => setMode('')} onSubmit={async (note) => { await boardAPI.closeFollowUp(f.id, note); onChanged(); }} />
      )}
      {mode === 'delegate' && (
        <NoteAction label="Hand off" placeholder="What should they do? They will see only this, not the answers."
          onCancel={() => setMode('')}
          onSubmit={async (note) => {
            if (!assignee) { const e = new Error(); e.response = { data: { detail: 'Choose a staff member.' } }; throw e; }
            await boardAPI.delegate(f.id, assignee, note);
            onChanged();
          }}>
          <StaffSelect staff={staff} value={assignee} onChange={setAssignee} />
        </NoteAction>
      )}
    </div>
  );
};

// A job that came out of feedback: publish a testimonial, call a referral, or a call handed down.
const TaskCard = ({ t, staff, canAssign, onChanged }) => {
  const [mode, setMode] = useState('');
  return (
    <div style={cardStyle(t.kind)}>
      <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <FiStar size={13} color="#f59e0b" /> {t.task_label} <KindChip kind={t.kind} />
      </div>
      <div style={{ color: '#94a3b8', fontSize: 11 }}>
        {t.name} · {t.assignee_name ? `assigned to ${t.assignee_name}` : 'not assigned yet'}
      </div>
      <p style={{ margin: '6px 0 0', color: '#334155', fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{t.detail}</p>
      <Contact name={t.contact_name} phone={t.contact_phone} />
      {!mode && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button className="cv-inline-btn" onClick={() => setMode('done')}><FiCheck size={11} /> Mark done</button>
          {canAssign && <button className="cv-inline-btn" onClick={() => setMode('assign')}>{t.assignee ? 'Reassign' : 'Assign'}</button>}
        </div>
      )}
      {mode === 'done' && (
        <NoteAction label="Mark done" placeholder="What was done? e.g. Published on the website"
          onCancel={() => setMode('')} onSubmit={async (note) => { await boardAPI.finishTask(t.id, note); onChanged(); }} />
      )}
      {mode === 'assign' && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
          <StaffSelect staff={staff} value="" onChange={async (id) => { if (id) { await boardAPI.assignTask(t.id, id); onChanged(); } }} />
          <button className="cv-inline-btn" onClick={() => setMode('')}>Cancel</button>
        </div>
      )}
    </div>
  );
};

const ReminderCard = ({ r, onOpen, onChanged }) => {
  const k = REMINDERS[r.kind] || REMINDERS.DUTY;
  const [busy, setBusy] = useState(false);
  const tick = async () => {
    setBusy(true);
    try { await dutiesAPI.tick(r.tick.duty, r.tick.period_start); onChanged(); } catch { setBusy(false); }
  };
  return (
    <div style={{ ...cardStyle(null), borderLeft: `4px solid ${k.color}`, background: r.overdue ? '#fff5f5' : '#fff' }}>
      <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <FiBell size={12} color={k.color} /> {r.title}
        <span style={{ background: k.tint, color: k.color, fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px' }}>{k.label}</span>
      </div>
      <div style={{ color: '#94a3b8', fontSize: 11 }}>{r.sub}</div>
      <Reasons items={r.reasons} />
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {r.tick && <button className="cv-inline-btn" onClick={tick} disabled={busy}><FiCheck size={11} /> Done</button>}
        <button className="cv-inline-btn" onClick={onOpen}>Open</button>
      </div>
    </div>
  );
};

const ActionBoard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (agentId) params.agent = agentId;
      if (kind) params.kind = kind;
      const res = await boardAPI.load(params);
      setData(res.data);
      if (res.data.agents?.length) setAgents(res.data.agents);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load the action board.');
    } finally {
      setLoading(false);
    }
  }, [agentId, kind]);

  useEffect(() => { load(); }, [load]);

  const pin = async (card, quadrant) => {
    try {
      await boardAPI.pin(card.kind, card.id, quadrant);
      load();
    } catch {
      setError('Failed to pin this client.');
    }
  };

  const kindCounts = data?.kind_counts || {};
  const total = KIND_ORDER.reduce((a, k) => a + (kindCounts[k] || 0), 0);
  const breakdown = KIND_ORDER.filter((k) => kindCounts[k]).map((k) => `${kindCounts[k]} ${kindCounts[k] === 1 ? KINDS[k].label.toLowerCase() : KINDS[k].plural}`);

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiGrid size={22} color="#011ff3" /> Action Board
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Every live client, new lead and your own duties, placed by urgency × importance. Quadrant is derived from their state; "Pin" overrides it.
          </p>
        </div>
        <div className="header-actions-right">
          <select className="filter-select" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">All client types</option>
            {KIND_ORDER.map((k) => (
              <option key={k} value={k}>{KINDS[k].plural.charAt(0).toUpperCase() + KINDS[k].plural.slice(1)}</option>
            ))}
          </select>
          {agents.length > 0 && (
            <select className="filter-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">All agents</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, margin: '12px 0', fontSize: 12.5, color: '#475569' }}>
        {KIND_ORDER.map((k) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: KINDS[k].color, display: 'inline-block' }} />
            {KINDS[k].label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiStar size={13} color="#f59e0b" /> From client feedback
        </span>
        {Object.entries(REMINDERS).map(([key, k]) => (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FiBell size={12} color={k.color} /> {k.label}
          </span>
        ))}
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertTriangle size={16} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      {loading && !data ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {QUADRANTS.map((q) => {
            // Feedback first: it is the newest reason a card is in this quadrant.
            const items = [...(data?.[q.key] || [])].sort((a, b) => (a.card === 'client') - (b.card === 'client'));
            return (
              <div key={q.key} style={{ background: q.bg, border: `1px solid ${q.color}40`, borderRadius: 14, padding: 16, minHeight: 200 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <span style={{ color: q.color, fontWeight: 700, fontSize: 15 }}>{q.key} · {q.title}</span>
                    <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 8 }}>{q.sub}</span>
                  </div>
                  <span style={{ color: q.color, fontWeight: 700, fontSize: 18 }}>{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div style={{ color: '#cbd5e1', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Nothing here.</div>
                ) : (
                  items.map((c) => {
                    if (c.card === 'feedback') {
                      return <FeedbackCard key={`f${c.id}`} f={c} staff={data.assignable} onOpen={() => navigate('/feedback')} onChanged={load} />;
                    }
                    if (c.card === 'task') {
                      return <TaskCard key={`t${c.id}`} t={c} staff={data.assignable} canAssign={data.feedback_leadership} onChanged={load} />;
                    }
                    if (c.card === 'reminder') {
                      return <ReminderCard key={c.id} r={c} onOpen={() => navigate(c.link)} onChanged={load} />;
                    }
                    return (
                      <ClientCard key={`${c.kind}${c.id}`} s={c}
                        onOpen={() => navigate(KINDS[c.kind]?.path || '/students')}
                        onPin={(quad) => pin(c, quad)} />
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      )}

      {data && (
        <p style={{ color: '#94a3b8', fontSize: 12 }}>
          {total} live client{total === 1 ? '' : 's'} on the board{breakdown.length ? `: ${breakdown.join(', ')}` : ''}. Thresholds are tunable in Settings.
        </p>
      )}
    </div>
  );
};

export default ActionBoard;
